import React, { useEffect } from "react";

import { useForm } from "react-hook-form";

import { zodResolver } from "@hookform/resolvers/zod";
import { FileText, Loader2, Truck } from "lucide-react";
import { z } from "zod";

import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/components/ui/form";
import { Input } from "~/components/ui/input";
import { Textarea } from "~/components/ui/textarea";
import { useLastTransportData } from "~/pages/operations/hooks/useLastTransportData";
import { SalesRequest } from "~/types/client";
import { TransportData } from "~/types/dispatch";
import { formatRut, isValidRut } from "~/utils/helpers";


interface CreateDispatchGuideDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (transport: TransportData, observations: string) => void;
  selectedOrder: SalesRequest;
  isCreating?: boolean;
}

// SII Res. 52 / 154 requires the guía de despacho to carry transport data, so the
// 5 fields below are mandatory. RUTs are validated with the módulo 11 check digit.
const dispatchGuideSchema = z.object({
  carrierName: z.string().trim().min(1, "Ingresa el nombre del transporte"),
  carrierRut: z
    .string()
    .trim()
    .min(1, "Ingresa el RUT del transporte")
    .refine(isValidRut, "RUT inválido"),
  driverName: z.string().trim().min(1, "Ingresa el nombre del conductor"),
  driverRut: z
    .string()
    .trim()
    .min(1, "Ingresa el RUT del conductor")
    .refine(isValidRut, "RUT inválido"),
  vehiclePlate: z.string().trim().min(1, "Ingresa la patente"),
  observations: z.string().optional(),
});

type DispatchGuideFormData = z.infer<typeof dispatchGuideSchema>;

const EMPTY_VALUES: DispatchGuideFormData = {
  carrierName: "",
  carrierRut: "",
  driverName: "",
  driverRut: "",
  vehiclePlate: "",
  observations: "",
};

export const CreateDispatchGuideDialog: React.FC<
  CreateDispatchGuideDialogProps
> = ({ isOpen, onClose, onConfirm, selectedOrder, isCreating = false }) => {
  const { data: lastTransport } = useLastTransportData(
    isOpen ? selectedOrder?.client_id : null
  );

  const form = useForm<DispatchGuideFormData>({
    resolver: zodResolver(dispatchGuideSchema),
    defaultValues: EMPTY_VALUES,
  });

  // Prefill with the client's last transport data each time the dialog opens.
  useEffect(() => {
    if (!isOpen) return;
    form.reset({
      ...EMPTY_VALUES,
      carrierName: lastTransport?.carrierName ?? "",
      carrierRut: lastTransport?.carrierRut ?? "",
      driverName: lastTransport?.driverName ?? "",
      driverRut: lastTransport?.driverRut ?? "",
      vehiclePlate: lastTransport?.vehiclePlate ?? "",
    });
  }, [isOpen, lastTransport, form]);

  const handleSubmit = (values: DispatchGuideFormData) => {
    onConfirm(
      {
        carrierName: values.carrierName.trim(),
        carrierRut: formatRut(values.carrierRut),
        driverName: values.driverName.trim(),
        driverRut: formatRut(values.driverRut),
        vehiclePlate: values.vehiclePlate.trim().toUpperCase(),
      },
      (values.observations ?? "").trim()
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Crear Guía de Despacho
          </DialogTitle>
          <DialogDescription>
            Ingresa los datos de transporte exigidos por el SII. Todos los
            campos son obligatorios.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
              <Truck className="h-4 w-4 text-gray-500" />
              Datos de transporte
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="carrierName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nombre del transporte</FormLabel>
                    <FormControl>
                      <Input
                        className="h-9"
                        placeholder="Transportes XYZ Ltda."
                        disabled={isCreating}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="carrierRut"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>RUT del transporte</FormLabel>
                    <FormControl>
                      <Input
                        className="h-9"
                        placeholder="76.123.456-7"
                        disabled={isCreating}
                        {...field}
                        onBlur={(e) => {
                          field.onChange(formatRut(e.target.value));
                          field.onBlur();
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="driverName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nombre del conductor</FormLabel>
                    <FormControl>
                      <Input
                        className="h-9"
                        placeholder="Juan Pérez González"
                        disabled={isCreating}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="driverRut"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>RUT del conductor</FormLabel>
                    <FormControl>
                      <Input
                        className="h-9"
                        placeholder="12.345.678-9"
                        disabled={isCreating}
                        {...field}
                        onBlur={(e) => {
                          field.onChange(formatRut(e.target.value));
                          field.onBlur();
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="vehiclePlate"
                render={({ field }) => (
                  <FormItem className="sm:col-span-2">
                    <FormLabel>Patente del vehículo</FormLabel>
                    <FormControl>
                      <Input
                        className="h-9 uppercase sm:max-w-[200px]"
                        placeholder="ABCD12"
                        disabled={isCreating}
                        {...field}
                        onChange={(e) =>
                          field.onChange(e.target.value.toUpperCase())
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="observations"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Observaciones (opcional)</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Instrucciones especiales para la guía de despacho..."
                      rows={3}
                      disabled={isCreating}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                className="h-9"
                onClick={onClose}
                disabled={isCreating}
              >
                Cancelar
              </Button>
              <Button type="submit" className="h-9" disabled={isCreating}>
                {isCreating ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creando...
                  </>
                ) : (
                  "Crear Guía de Despacho"
                )}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
